/**
 * @fileoverview TipTap extension to preserve inline styles
 *
 * By default TipTap strips the `style` attribute from parsed HTML.
 * This extension adds `style` as a global attribute on the node types
 * that don't otherwise own their inline CSS, so colours/widths/margins
 * etc. survive the parse → serialize round-trip.
 *
 * IMPORTANT: `horizontalRule` is intentionally excluded — the
 * `EditableHorizontalRule` node already serialises its own `style`
 * attribute from `color`/`thickness`/`width`/`alignment`. If we also
 * captured the raw `style` here, edits to those attrs wouldn't update
 * the cached raw `style`, TipTap would merge both `style`s on render,
 * and on the next parse `parseHrStyle`'s regex would pick the FIRST
 * `background:` it sees (the stale one) — so HR edits would silently
 * revert on the next round-trip.
 *
 * IMPORTANT: For `paragraph` / `heading` nodes we strip CSS properties
 * owned by sibling extensions (`line-height` via `LineHeight`; `margin-top` /
 * `margin-bottom` via `ParagraphSpacing`; `margin-left` via `BlockIndent`;
 * `text-align` via `TextAlign`). If we don't, multiple extensions register a
 * `style` global attribute on the same node type and TipTap merges them with
 * `; `. PreserveStyles is registered *after* those extensions, so a stale
 * captured `line-height`, margin or `text-align` can end up *last* in the
 * cascade and win via CSS last-rule, silently reverting toolbar edits (line
 * spacing, paragraph spacing, indent, or text alignment).
 */

import { Extension } from "@tiptap/core";

/**
 * CSS properties on paragraph/heading nodes that are owned by other
 * extensions and must NOT be captured here. Lower-cased.
 */
const PARAGRAPH_OWNED_BY_OTHER_EXTENSIONS = [
  "line-height",
  "margin-top",
  "margin-bottom",
  "margin-left",
  "text-align",
];

export interface PreserveStylesOptions {
  /**
   * Paragraph/heading CSS properties to drop, because a sibling extension
   * owns them. Editors that don't register those extensions (e.g. the mail
   * signature editor) pass `[]` for full passthrough.
   */
  paragraphStripProps: string[];
}

/**
 * Strip declarations whose property name appears in `propsToStrip` from a
 * CSS `style` string. Returns `null` if nothing remains so the renderer
 * can omit the `style` attribute entirely.
 */
function stripStyleProperties(
  styleString: string | null,
  propsToStrip: string[],
): string | null {
  if (!styleString) return null;
  const kept = styleString
    .split(";")
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => {
      const colon = decl.indexOf(":");
      if (colon === -1) return true;
      const prop = decl.slice(0, colon).trim().toLowerCase();
      return !propsToStrip.includes(prop);
    });
  if (kept.length === 0) return null;
  return kept.join("; ");
}

export const PreserveStyles = Extension.create<PreserveStylesOptions>({
  name: "preserveStyles",

  addOptions() {
    return { paragraphStripProps: PARAGRAPH_OWNED_BY_OTHER_EXTENSIONS };
  },

  addGlobalAttributes() {
    const stripProps = this.options.paragraphStripProps;
    return [
      {
        // Paragraph / heading: capture inline styles BUT strip any property
        // that's owned by a sibling extension (see top-of-file comment).
        types: ["paragraph", "heading"],
        attributes: {
          style: {
            default: null,
            parseHTML: (el: HTMLElement) =>
              stripStyleProperties(el.getAttribute("style"), stripProps),
            renderHTML: (attrs: Record<string, unknown>) => {
              const style = stripStyleProperties(
                typeof attrs.style === "string" ? attrs.style : null,
                stripProps,
              );
              if (!style) return {};
              return { style };
            },
          },
        },
      },
      {
        // Tables and images don't have sibling extensions claiming any of
        // their CSS properties, so full passthrough is safe.
        types: ["table", "tableRow", "tableCell", "tableHeader", "image"],
        attributes: {
          style: {
            default: null,
            parseHTML: (el: HTMLElement) => el.getAttribute("style") || null,
            renderHTML: (attrs: Record<string, unknown>) => {
              if (!attrs.style) return {};
              return { style: attrs.style };
            },
          },
        },
      },
    ];
  },
});
